import React from 'react';
import { LayoutSkeleton } from './SidebarSkeleton';
import { 
  DashboardViewSkeleton,
  PatientViewSkeleton,
  DoctorViewSkeleton,
  AppointmentViewSkeleton,
  BillingViewSkeleton,
  ReportsViewSkeleton,
  SettingsViewSkeleton,
  FormViewSkeleton,
  DetailViewSkeleton
} from './ViewSpecificSkeletons';

// Doctor-specific Dashboard Skeleton
export const DoctorDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Doctor Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Today's Schedule */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-gray-300 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                    <div className="w-48 h-3 bg-gray-300 rounded animate-pulse mb-1"></div>
                    <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                  <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Queue */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                    <div>
                      <div className="w-24 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                      <div className="w-20 h-3 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="w-16 h-6 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Nurse-specific Dashboard Skeleton
export const NurseDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Nurse Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Ward Management */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
                    <div className="w-16 h-6 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full h-3 bg-gray-300 rounded animate-pulse"></div>
                    <div className="w-3/4 h-3 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Care Tasks */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                    <div>
                      <div className="w-28 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                      <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Receptionist-specific Dashboard Skeleton
export const ReceptionistDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Reception Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse mb-3"></div>
                <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Today's Appointments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-gray-300 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                    <div className="w-48 h-3 bg-gray-300 rounded animate-pulse mb-1"></div>
                    <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                  <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Check-ins */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                    <div>
                      <div className="w-28 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                      <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Accountant-specific Dashboard Skeleton
export const AccountantDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-24 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Financial Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="w-full h-80 bg-gray-300 rounded animate-pulse"></div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="w-full h-80 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <th key={index} className="px-6 py-3 text-left">
                      <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {Array.from({ length: 6 }).map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Admin-specific Dashboard Skeleton
export const AdminDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* System Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* System Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                    <div>
                      <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                      <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="w-16 h-6 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-300 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <div className="w-3/4 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                    <div className="w-1/2 h-3 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                  <div className="w-16 h-3 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Patient-specific Dashboard Skeleton
export const PatientDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-64 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-24 h-8 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-32 h-8 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Patient Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Upcoming Appointments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gray-300 rounded-full animate-pulse"></div>
                <div className="flex-1">
                  <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                  <div className="w-48 h-3 bg-gray-300 rounded animate-pulse mb-1"></div>
                  <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                </div>
                <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Medical History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="w-48 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                  <div>
                    <div className="w-28 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                    <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                </div>
                <div className="w-20 h-8 bg-gray-300 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};
